import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconRight,
  disabled,
  className = '',
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center gap-2 font-semibold rounded-xl ' +
    'focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
    'transition-all duration-200 active:scale-[0.97] select-none ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

  const variants = {
    primary:
      'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500/40 ' +
      'shadow-sm hover:shadow-glow-sm',
    secondary:
      'bg-slate-100 hover:bg-slate-200 text-slate-800 focus:ring-slate-300',
    outline:
      'border border-primary-200 text-primary-700 bg-transparent ' +
      'hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500/30',
    ghost:
      'text-slate-700 hover:bg-slate-100 bg-transparent focus:ring-slate-300',
    danger:
      'bg-red-600 hover:bg-red-700 text-white focus:ring-red-400 shadow-sm',
  };

  const sizes = {
    sm: 'px-3.5 py-2 text-sm min-h-[38px]',
    md: 'px-5 py-2.5 text-sm min-h-[42px]',
    lg: 'px-6 py-3 text-base min-h-[48px]',
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <span className="h-4 w-4 rounded-full border-2 border-current border-b-transparent animate-spin flex-shrink-0" />
      ) : (
        icon && <span className="flex-shrink-0 flex items-center">{icon}</span>
      )}
      {children}
      {!loading && iconRight && (
        <span className="flex-shrink-0 flex items-center">{iconRight}</span>
      )}
    </button>
  );
};

export default Button;
